// Manages active AJAX requests and automatically prompts the user to log in as needed
import $ from 'jquery'
import _ from 'underscore'
import session from '@/auth/models/session'
import authChannel from '@/auth/channel'
import { getSigninUiOptions } from '@/utils/signinUiOptions'

const signIn = (originalOptions) =>
  authChannel.request('signin', { ...originalOptions.signinOptions })

let activeRequests = []

const paperCultureUrlPattern =
  /^((https?:)?\/\/[a-z]+\.paperculture\.com)?\/[a-z]+/

let blockPageUnload = false

const isSignInHtmlResponse = (html) =>
  typeof html === 'string' && html.indexOf('Sign In | Paper Culture') > 0

/**
 * The change password API response is not AJAX-friendly so we need to detect the sign in page
 * to know if the session is not valid.
 */
const isChangePasswordUnauthenticated = (html, url, method) => {
  return (
    isSignInHtmlResponse(html) &&
    url === '/cards/myaccount.html?mode=changepassword&vid=20090115001' &&
    method === 'POST'
  )
}

// Override $.ajax to pre-authenticate requests if options.authenticate is true
const oldAjax = $.ajax
$.ajax = function (url, options) {
  options = options || {}
  // Support omitting `url` and passing options as the first argument (copied from $.ajax)
  if (typeof url === 'object') {
    options = url
    // Don't set url to undefined or it won't be available to pass through
  } else {
    // Make sure $.ajaxPrefilter sees this in originalOptions or else
    // we might make a subsequent request with a URL param which will
    // make an AJAX request to the current page instead of the intended URL.
    options.url = url
  }

  if (options.authenticate) {
    // Wait for session to be initialized, once it returns whether we're signed in,
    // overwrite the success response with either normal AJAX or a signin promise.
    // We don't override promise if getting the signin status fails.

    if (session.isSignedIn()) {
      // Execute the normal AJAX request because we're already signed in
      // eslint-disable-next-line prefer-rest-params -- FIXME
      return oldAjax.apply(this, arguments)
    } else {
      // The request must be authenticated but the user isn't signed in.
      // Before submitting the request, launch signin and ensure that it is successful.

      // If the sign-in is successful, return the promise from an AJAX request.
      // Otherwise, if the sign-in failed, just pass the rejection from signin through to the caller.
      return signIn(options).then(() =>
        // Now, we're signed in, so retry the authenticated request
        $.ajax(options)
      )
    }
  } else {
    // Proxy the call straight through to normal $.ajax
    // eslint-disable-next-line prefer-rest-params -- FIXME
    return oldAjax.apply(this, arguments)
  }
}

// `options` are the request options
// `originalOptions` are the options as provided to the ajax method,
// unmodified and, thus, without defaults from `ajaxSettings`
$.ajaxPrefilter(function (options, originalOptions, jqXHR) {
  // Don't override or monitor non-Paper Culture requests.
  // This ensures we don't interfere with third-party libraries using jQuery
  // and that we don't leak the user's security token.
  if (!paperCultureUrlPattern.test(options.url)) {
    return
  }

  // The promise for this will be returned from $.ajax instead of the jqXHR
  const dfd = $.Deferred()

  // Keep track of active Paper Culture AJAX requests (exlcuding external ones)
  activeRequests.push(dfd)
  dfd.always(() => (activeRequests = _.without(activeRequests, dfd)))

  // Set up-to-date authorization header with secure Paper Culture requests.
  // This doesn't account for CORS requests to http from https, but those should never happen.
  if (originalOptions.authenticate && location.protocol === 'https:') {
    // Use the Salesforce session id as the authentication token if present
    if (originalOptions.salesforceSessionId) {
      jqXHR.setRequestHeader(
        'Authorization',
        'Bearer ' + originalOptions.salesforceSessionId
      )
    } else {
      const authHeader = session.getAuthorizationHeader()
      if (authHeader) {
        jqXHR.setRequestHeader('Authorization', authHeader)
      }
    }
  }

  if (originalOptions.authenticate && !originalOptions.signinOptions) {
    // Capture the current signin UI options when the AJAX request is initiated
    originalOptions = {
      ...originalOptions,
      signinOptions: getSigninUiOptions()
    }
  }

  // Copy the type of request to the Deferred wrapper so
  // we can detect what types of requests are in flight.
  dfd._type = options.type

  // Resolve our Deferred wrapper with then jqXHR is successful
  jqXHR.done(function (data) {
    if (
      originalOptions.authenticate &&
      (isChangePasswordUnauthenticated(
        data,
        originalOptions.url,
        originalOptions.type
      ) ||
        data?.result?.loggedIn === false)
    ) {
      // Successful response. However the request must be authenticated and the user isn't logged in.
      return signIn(originalOptions)
        .fail(dfd.reject)
        .done(() =>
          // Now we're logged in so retry the authenticated request
          $.ajax(originalOptions).then(dfd.resolve, dfd.reject)
        )
    } else {
      // Success
      // eslint-disable-next-line prefer-rest-params -- FIXME
      return dfd.resolve.apply(this, arguments)
    }
  })

  // Handle an AJAX error
  jqXHR.fail(function () {
    if (jqXHR.status === 401) {
      // AJAX request failed because it was unauthorized so we display signin automatically
      // even if authenticate isn't true
      return signIn(originalOptions)
        .fail(dfd.reject)
        .done(() =>
          // Now we're logged in so retry the authenticated request
          $.ajax(_.extend(originalOptions, { authenticate: true })).then(
            dfd.resolve,
            dfd.reject
          )
        )
    } else {
      // Pass the error through to the deferred proxy
      // eslint-disable-next-line prefer-rest-params -- FIXME
      return dfd.rejectWith(jqXHR, arguments)
    }
  })

  // Copy promise methods from our dfd to the jqXHR. While we can't change the object
  // that's returned by $.ajax here, we can override its properties.
  // https://www.bennadel.com/blog/2132-using-ajaxprefilter-to-configure-ajax-requests-in-jquery-1-5.htm
  jqXHR = dfd.promise(jqXHR)
  // At this point, the Promise-based methods of the jqXHR are actually the locally-declared ones.
  // Now, we just have to point the success and error methods (AJAX related)
  // to the done and fail methods (promise related).
  jqXHR.success = jqXHR.done
  return (jqXHR.error = jqXHR.fail)
})

// Expose some methods to access requests in flight
const api = {
  hasActiveRequests(updatesOnly) {
    return this.getActiveRequests(updatesOnly).length
  },

  // If `updatesOnly` is true, we'll only count non-GET requests (that are changing data).
  getActiveRequests(updatesOnly) {
    if (updatesOnly) {
      return _.filter(activeRequests, (request) => request._type !== 'GET')
    } else {
      return activeRequests
    }
  },

  activeRequestsPromise(updatesOnly) {
    // eslint-disable-next-line prefer-spread -- FIXME
    return $.when.apply($, this.getActiveRequests(updatesOnly))
  },

  // Prompts the user before unloading the page if there are pending requests
  blockPageUnload(isBlockedOnPendingRequests) {
    return (blockPageUnload = isBlockedOnPendingRequests)
  }
}

// If there are requests in flight that are saving changes (non-GET), prompt the user if they try to leave
$(window).on('beforeunload', function () {
  if (blockPageUnload && api.hasActiveRequests(true)) {
    return 'Changes are still saving.'
  }
})

export default api
